import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, QrCode, Hash } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';

const LinkDevice = () => {
  const navigate = useNavigate();
  const { linkDeviceWithQR, linkDeviceWithCode } = useAuth();
  const [method, setMethod] = useState(null); // 'qr' or 'code'
  const [pairingCode, setPairingCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  const startQRScanner = async () => {
    setScanning(true);
    try {
      const html5QrCode = new Html5Qrcode("qr-reader");
      
      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        async (decodedText) => {
          await html5QrCode.stop();
          setScanning(false);
          
          setLoading(true);
          const result = await linkDeviceWithQR(decodedText);
          setLoading(false);
          
          if (result.success) {
            toast.success('Device linked successfully!');
            navigate('/chats');
          } else {
            toast.error(result.error);
          }
        }
      );
    } catch (err) {
      console.error('QR Scanner error:', err);
      setScanning(false);
      toast.error('Failed to start camera. Please check permissions.');
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    
    if (pairingCode.length !== 6) {
      toast.error('Pairing code must be 6 digits');
      return;
    }

    setLoading(true);
    const result = await linkDeviceWithCode(pairingCode);
    setLoading(false);

    if (result.success) {
      toast.success('Device linked successfully!');
      navigate('/chats');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-background-DEFAULT flex flex-col" data-testid="link-device-page">
      <div className="p-6">
        <button
          data-testid="back-btn"
          onClick={() => navigate('/')}
          className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-secondary-DEFAULT transition-colors"
        >
          <ArrowLeft size={20} className="text-text-primary" />
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {!method ? (
            <div className="bg-white rounded-3xl p-8 shadow-card border border-border-DEFAULT">
              <div className="text-center mb-8">
                <h2 className="font-heading text-3xl text-text-primary mb-2">Link Device</h2>
                <p className="text-text-secondary text-sm">Choose a method to link your device</p>
              </div>

              <div className="space-y-4">
                <button
                  data-testid="qr-method-btn"
                  onClick={() => setMethod('qr')}
                  className="w-full bg-white rounded-2xl p-6 shadow-sm border border-border-DEFAULT hover:border-primary hover:shadow-md transition-all cursor-pointer flex items-center gap-4"
                >
                  <div className="w-12 h-12 rounded-full bg-secondary-DEFAULT flex items-center justify-center">
                    <QrCode size={24} className="text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-medium text-text-primary mb-1">Scan QR Code</h3>
                    <p className="text-sm text-text-secondary">Scan from another device</p>
                  </div>
                </button>

                <button
                  data-testid="code-method-btn"
                  onClick={() => setMethod('code')}
                  className="w-full bg-white rounded-2xl p-6 shadow-sm border border-border-DEFAULT hover:border-primary hover:shadow-md transition-all cursor-pointer flex items-center gap-4"
                >
                  <div className="w-12 h-12 rounded-full bg-secondary-DEFAULT flex items-center justify-center">
                    <Hash size={24} className="text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-medium text-text-primary mb-1">Enter Pairing Code</h3>
                    <p className="text-sm text-text-secondary">6-digit code from your device</p>
                  </div>
                </button>
              </div>
            </div>
          ) : method === 'qr' ? (
            <div className="bg-white rounded-3xl p-8 shadow-card border border-border-DEFAULT">
              <div className="text-center mb-6">
                <h2 className="font-heading text-2xl text-text-primary mb-2">Scan QR Code</h2>
                <p className="text-text-secondary text-sm">Position the QR code within the frame</p>
              </div>

              {!scanning ? (
                <button
                  data-testid="start-scan-btn"
                  onClick={startQRScanner}
                  className="w-full h-12 px-8 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium shadow-gold hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                >
                  Start Camera
                </button>
              ) : (
                <div id="qr-reader" className="rounded-2xl overflow-hidden border-2 border-primary"></div>
              )}

              <button
                onClick={() => setMethod(null)}
                className="w-full mt-4 h-10 text-text-secondary hover:text-primary transition-colors"
              >
                Back to methods
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-8 shadow-card border border-border-DEFAULT">
              <div className="text-center mb-6">
                <h2 className="font-heading text-2xl text-text-primary mb-2">Enter Pairing Code</h2>
                <p className="text-text-secondary text-sm">Enter the 6-digit code from your device</p>
              </div>

              <form onSubmit={handleCodeSubmit} className="space-y-5">
                <div>
                  <input
                    data-testid="pairing-code-input"
                    type="text"
                    value={pairingCode}
                    onChange={(e) => setPairingCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full h-14 bg-[#F9F9F9] border-none rounded-xl px-4 text-center text-2xl tracking-widest text-[#1A1A1A] placeholder:text-[#999999] focus:ring-2 focus:ring-[#D4AF37] transition-all font-mono"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>

                <button
                  data-testid="verify-code-btn"
                  type="submit"
                  disabled={loading || pairingCode.length !== 6}
                  className="w-full h-12 px-8 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium shadow-gold hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Link Device'}
                </button>
              </form>

              <button
                onClick={() => setMethod(null)}
                className="w-full mt-4 h-10 text-text-secondary hover:text-primary transition-colors"
              >
                Back to methods
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default LinkDevice;